import {
  EOrganization,
  ERate,
  ESnapshot,
  ESnapshotExists,
  exists
} from 'lib/types';
import { CardGridLayout } from 'lib/components/Card/Grid';
import { OrganizationType, Product } from 'lib/enums';
import { getModelFromSnapshot } from 'lib/model';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import { getFirebaseContext } from 'utils/firebase';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { CustomerObjDataFields } from './CreateOrEditModalDetails';
import {
  BillingTermSettings,
  CustomerAffidavitSettings,
  CustomerDiscountSettings
} from './shared';
import { CustomerNoticeRatesSection } from './shared/CustomerNoticeRatesSection';
import { ProductRatesSettings } from './shared/ProductRatesSettings';

type AllowAllOrganizationTypes = { type: 'all' };
type OrganizationTypeWhiteList = {
  type: 'whitelist';
  organizationTypes: number[];
};
type OrganizationTypeBlackList = {
  type: 'blacklist';
  organizationTypes: number[];
};
type OrganizationTypeFilter =
  | AllowAllOrganizationTypes
  | OrganizationTypeWhiteList
  | OrganizationTypeBlackList;

type RenderIfRelevantToCustomerProps = {
  organizationTypeFilter: OrganizationTypeFilter;
  organization: EOrganization | null;
  children: React.ReactNode;
};

function RenderIfRelevantToCustomer({
  organizationTypeFilter,
  organization,
  children
}: RenderIfRelevantToCustomerProps) {
  if (organizationTypeFilter.type === 'all') {
    return <>{children}</>;
  }

  const { organizationTypes } = organizationTypeFilter;
  const { organizationType = 0 } = organization || {};
  const hasRelevantType = organizationTypes.includes(organizationType);

  if (organizationTypeFilter.type === 'whitelist') {
    if (!hasRelevantType) {
      return null;
    }
  }

  if (organizationTypeFilter.type === 'blacklist') {
    if (hasRelevantType) {
      return null;
    }
  }

  return <>{children}</>;
}

export type EditCustomerOrganizationModalSettingsProps = {
  value: CustomerObjDataFields;
  onChange: (value: CustomerObjDataFields) => unknown;
  activeOrganization: ESnapshot<EOrganization> | null;
  clientOrganization: ESnapshot<EOrganization>;
  rates?: ESnapshotExists<ERate>[];
};

export function EditCustomerOrganizationModalSettings({
  value,
  onChange,
  activeOrganization,
  clientOrganization,
  rates
}: EditCustomerOrganizationModalSettingsProps) {
  const renderForAll: AllowAllOrganizationTypes = { type: 'all' };
  const renderIfNotFuneralHome: OrganizationTypeBlackList = {
    type: 'blacklist',
    organizationTypes: [OrganizationType.funeral_home.value]
  };
  const renderIfFuneralHome: OrganizationTypeWhiteList = {
    type: 'whitelist',
    organizationTypes: [OrganizationType.funeral_home.value]
  };

  if (!activeOrganization || !exists(activeOrganization)) {
    return <LoadingSpinner />;
  }

  const organizationModel = getModelFromSnapshot(
    OrganizationModel,
    getFirebaseContext(),
    activeOrganization
  );

  const hasClassifiedsEnabled = organizationModel.hasAdTypeActive(
    Product.Classified
  );

  return (
    <>
      <RenderIfRelevantToCustomer
        organizationTypeFilter={renderForAll}
        organization={clientOrganization.data() || null}
      >
        <CardGridLayout
          header={{
            title: "Set up this organization's preferences",
            description:
              'Configure how you want this organization to interact with your publication.'
          }}
        >
          <BillingTermSettings
            activeOrganization={activeOrganization}
            onChange={(billingTerm: number) =>
              onChange({ ...value, billingTerm })
            }
            value={value.billingTerm}
          />
        </CardGridLayout>
      </RenderIfRelevantToCustomer>

      <RenderIfRelevantToCustomer
        organizationTypeFilter={renderIfNotFuneralHome}
        organization={clientOrganization.data() || null}
      >
        <CardGridLayout
          header={{
            title: 'Public Notice Settings',
            description:
              'Configure settings for public notices for this customer organization.'
          }}
        >
          <CustomerNoticeRatesSection
            value={value}
            onChange={onChange}
            rates={rates || []}
            activeOrganization={activeOrganization}
          />
          <CustomerAffidavitSettings
            activeOrganization={activeOrganization}
            onChange={onChange}
            value={value}
          />
          <CustomerDiscountSettings
            onChange={discountConfig => onChange({ ...value, discountConfig })}
            value={value.discountConfig ?? {}}
          />
        </CardGridLayout>
      </RenderIfRelevantToCustomer>

      {hasClassifiedsEnabled && (
        <RenderIfRelevantToCustomer
          organizationTypeFilter={renderIfNotFuneralHome}
          organization={clientOrganization.data() || null}
        >
          <CardGridLayout
            header={{
              title: 'Classifieds Settings',
              description:
                'Configure settings for classifieds for this customer organization.'
            }}
          >
            <ProductRatesSettings
              productType={Product.Classified}
              value={value}
              onChange={onChange}
              activeOrganization={activeOrganization}
            />
          </CardGridLayout>
        </RenderIfRelevantToCustomer>
      )}

      <RenderIfRelevantToCustomer
        organizationTypeFilter={renderIfFuneralHome}
        organization={clientOrganization.data() || null}
      >
        <CardGridLayout
          header={{
            title: 'Obituaries Settings',
            description:
              'Configure settings for obituaries for this customer organization.'
          }}
        >
          <ProductRatesSettings
            productType={Product.Obituary}
            value={value}
            onChange={onChange}
            activeOrganization={activeOrganization}
          />
        </CardGridLayout>
      </RenderIfRelevantToCustomer>
    </>
  );
}
